import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { buttonVariants } from '@/components/ui/button'
import { footerNav, siteConfig } from '@/constants/site'

export const SiteFooter: FC = (): ReactNode => {
    const year = new Date().getFullYear()

    return (
        <footer className='bg-forest text-forest-foreground'>
            <div className='mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20'>
                <div className='grid gap-12 lg:grid-cols-12'>
                    <div className='lg:col-span-5'>
                        <Logo tone='light' />

                        <p className='mt-6 max-w-sm leading-relaxed text-forest-foreground/70'>
                            Timber tiny houses on wheels, designed with you and built by hand in our workshop.
                        </p>

                        <Link
                            href='/contact'
                            className={cn(buttonVariants({ variant: 'light' }), 'mt-8')}
                        >
                            Request a quote
                            <ArrowRight className='size-4' />
                        </Link>
                    </div>

                    <div className='grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-7'>
                        {footerNav.map(group => (
                            <div key={group.title}>
                                <h2 className='text-xs font-semibold tracking-[0.18em] text-forest-foreground/50 uppercase'>
                                    {group.title}
                                </h2>

                                <ul className='mt-5 space-y-3'>
                                    {group.items.map(item => (
                                        <li key={item.href}>
                                            <Link
                                                href={item.href}
                                                className='text-sm text-forest-foreground/80 transition-colors hover:text-forest-foreground'
                                            >
                                                {item.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}

                        <div className='col-span-2 sm:col-span-1'>
                            <h2 className='text-xs font-semibold tracking-[0.18em] text-forest-foreground/50 uppercase'>
                                Visit the workshop
                            </h2>

                            <p className='mt-5 text-sm leading-relaxed text-forest-foreground/80'>
                                See homes in progress and talk to the people who build them. Visits are by appointment.
                            </p>

                            <Link
                                href='/contact?visit=1'
                                className='mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-forest-foreground underline-offset-4 hover:underline'
                            >
                                Book a visit
                                <ArrowRight className='size-4' />
                            </Link>
                        </div>
                    </div>
                </div>

                <div className='mt-16 flex flex-col gap-3 border-t border-forest-foreground/15 pt-8 text-sm text-forest-foreground/60 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        &copy; <span suppressHydrationWarning>{year}</span> {siteConfig.name}. All rights reserved.
                    </p>

                    <p>
                        Built by hand, one home at a time.
                    </p>
                </div>
            </div>
        </footer>
    )
}
